import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import client, {
  API, formatApiError, getErrorOffender, fmtTime, fmtDay, dayKey, TYPE_LABEL, fmtHours,
  workedMinutes, hasUnclosedBreak, isoWeekStart, isoWeekId,
} from "../../lib/api";

function toLocalDatetimeInput(iso) {
  // Convert an ISO 8601 UTC string to a value suitable for <input type=datetime-local>
  const d = iso ? new Date(iso) : new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalDatetimeInput(v) {
  // datetime-local values are in local time. Convert back to UTC ISO.
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d)) return null;
  return d.toISOString();
}

const hhmm = (d) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function readConflict(e) {
  const d = e?.response?.data?.detail;
  if (e?.response?.status !== 400 || !d || typeof d !== "object" || !d.conflict) return null;
  return { message: d.message, day: d.day, offender: getErrorOffender(e) };
}

function ConflictPanel({ conflict, onFillDay, onFixBlocker }) {
  return (
    <div data-testid="conflict-panel" className="mb-4 rounded-xl border border-jewel-amber bg-panel-soft p-3">
      <div className="caps text-jewel-amber text-[11px] mb-1">Couldn't save — punches out of order</div>
      <p data-testid="conflict-message" className={`text-ivory text-[13px] leading-snug ${onFillDay || (conflict.offender && onFixBlocker) ? "mb-3" : ""}`}>{conflict.message}</p>
      <div className="flex flex-wrap gap-2">
        {onFillDay && (
          <button onClick={onFillDay} data-testid="conflict-fill-day" className="seal-primary rounded-full px-4 py-2 text-[13px] font-semibold">
            Fill whole day instead
          </button>
        )}
        {conflict.offender && onFixBlocker && (
          <button onClick={() => onFixBlocker(conflict.offender)} data-testid="conflict-fix-blocker" className="rounded-full border border-line text-muted-jewel px-4 py-2 text-[13px] hover:text-ivory transition-colors">
            Show the clashing punch
          </button>
        )}
      </div>
    </div>
  );
}

const PUNCH_TYPE_OPTIONS = [
  ["in", "Punch in"],
  ["out", "Punch out"],
];

function ManualPunchModal({ employee, onClose, onSaved, onOffenderJump, onFillDay }) {
  const [type, setType] = useState("in");
  const [when, setWhen] = useState(toLocalDatetimeInput());
  const [useNow, setUseNow] = useState(true);
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);

  const fillDay = () => {
    const chosen = useNow ? new Date() : new Date(when);
    const t = hhmm(chosen);
    const shift = (h) => hhmm(new Date(Math.min(chosen.getTime() + h * 3600000, new Date(chosen).setHours(23, 59, 0, 0))));
    const fromStart = type === "in" || type === "break_end";
    const earlier = hhmm(new Date(Math.max(chosen.getTime() - 8 * 3600000, new Date(chosen).setHours(0, 0, 0, 0))));
    onFillDay({
      employeeId: employee.id,
      date: ymd(chosen),
      inTime: fromStart ? t : earlier,
      outTime: fromStart ? shift(8) : t,
    });
  };
  const [recent, setRecent] = useState([]); // last ~10 events for this employee

  useEffect(() => {
    if (!employee?.id) return;
    // Load a small recent window to power sanity prompts (duplicate / long-session)
    const fromIso = new Date(Date.now() - 3 * 24 * 3600 * 1000).toISOString();
    client.get(`/events?employee_id=${employee.id}&date_from=${encodeURIComponent(fromIso)}`)
      .then(({ data }) => setRecent(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [employee?.id]);

  const save = async () => {
    setBusy(true);
    setConflict(null);
    try {
      const body = { employee_id: employee.id, type };
      const chosenIso = useNow ? new Date().toISOString() : fromLocalDatetimeInput(when);
      if (!chosenIso) { toast.error("Pick a valid time"); setBusy(false); return; }
      const chosen = new Date(chosenIso);
      const nowMs = Date.now();
      const diffMs = chosen.getTime() - nowMs;

      // (b) Future
      if (!useNow && diffMs > 60 * 1000) {
        if (!window.confirm(`That time is in the future (${chosen.toLocaleString()}). Are you sure?`)) { setBusy(false); return; }
      }
      // (b) Backdated more than 24 hours OR different local date from today
      if (!useNow) {
        const todayLocal = new Date().toDateString();
        const chosenLocal = chosen.toDateString();
        if (diffMs < -24 * 3600 * 1000) {
          const hoursBack = Math.round(-diffMs / 3600000);
          if (!window.confirm(`That's ${hoursBack} hours in the past (${chosenLocal}). Backdate the punch anyway?`)) { setBusy(false); return; }
        } else if (chosenLocal !== todayLocal) {
          if (!window.confirm(`That punch is dated ${chosenLocal} — not today (${todayLocal}). Save anyway?`)) { setBusy(false); return; }
        }
      }

      // Duplicate detection — same type within 60 s of an existing punch
      const dupe = recent.find((e) => e.type === type && Math.abs(new Date(e.ts).getTime() - chosen.getTime()) < 60000);
      if (dupe) {
        if (!window.confirm(`There's already a ${TYPE_LABEL[type]} recorded within a minute of that time. Record another one anyway?`)) { setBusy(false); return; }
      }

      // (a) Long-session warning when punching OUT
      if (type === "out") {
        // Last "in" before the chosen ts, without an intervening out
        const before = recent
          .filter((e) => new Date(e.ts).getTime() < chosen.getTime())
          .sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
        const lastRelevant = before.find((e) => e.type === "in" || e.type === "out");
        if (lastRelevant && lastRelevant.type === "in") {
          const hours = (chosen.getTime() - new Date(lastRelevant.ts).getTime()) / 3600000;
          if (hours > 12) {
            if (!window.confirm(`This session would span ${hours.toFixed(1)} hours since ${new Date(lastRelevant.ts).toLocaleString()}. Save anyway?`)) { setBusy(false); return; }
          }
        }
      }

      if (!useNow) body.ts = chosenIso;
      // Client TZ offset lets the backend format offender timestamps in local time
      // in error messages instead of raw UTC.
      body.tz_offset_min = -new Date().getTimezoneOffset();
      await client.post("/events/manual", body);
      toast.success(`${employee.name}: ${TYPE_LABEL[type]} recorded`);
      onSaved && onSaved();
    } catch (e) {
      const c = readConflict(e);
      if (c) { setConflict(c); return; }
      const offender = getErrorOffender(e);
      if (offender && onOffenderJump) {
        toast.error(formatApiError(e), {
          duration: 8000,
          action: { label: "Fix blocker", onClick: () => onOffenderJump(offender) },
        });
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
      data-testid="manual-punch-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-panel border border-line rounded-2xl w-full max-w-[480px] p-5"
        data-testid="manual-punch-modal"
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="caps text-muted-jewel">Owner punch</div>
            <div className="font-serif text-xl text-ivory">{employee.name}</div>
            <div className="text-[12px] text-muted-jewel">{employee.branch}{employee.state ? ` · currently ${employee.state === "in" ? "on the clock" : employee.state === "break" ? "on a break" : "off"}` : ""}</div>
          </div>
          <button onClick={onClose} data-testid="manual-punch-close" className="text-muted-jewel hover:text-ivory text-xl leading-none px-2">×</button>
        </div>

        <div className="mb-3">
          <div className="caps text-muted-jewel text-[11px] mb-1.5">Action</div>
          <div className="grid grid-cols-2 gap-2">
            {PUNCH_TYPE_OPTIONS.map(([k, l]) => (
              <button
                key={k}
                onClick={() => { setType(k); setConflict(null); }}
                data-testid={`manual-type-${k}`}
                className={`rounded-lg py-2.5 text-[14px] font-semibold border transition-colors ${
                  type === k
                    ? "seal-primary border-transparent"
                    : "bg-transparent border-line text-muted-jewel hover:text-ivory"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <div className="caps text-muted-jewel text-[11px] mb-1.5">When</div>
          <div className="flex gap-2 mb-2">
            <button
              onClick={() => { setUseNow(true); setWhen(toLocalDatetimeInput()); }}
              data-testid="manual-when-now"
              className={`rounded-full px-4 py-1.5 text-[13px] border transition-colors ${
                useNow ? "seal-primary border-transparent font-semibold" : "border-line text-muted-jewel hover:text-ivory"
              }`}
            >
              Now
            </button>
            <button
              onClick={() => setUseNow(false)}
              data-testid="manual-when-pick"
              className={`rounded-full px-4 py-1.5 text-[13px] border transition-colors ${
                !useNow ? "seal-primary border-transparent font-semibold" : "border-line text-muted-jewel hover:text-ivory"
              }`}
            >
              Pick time
            </button>
          </div>
          {!useNow && (
            <input
              type="datetime-local"
              value={when}
              onChange={(e) => { setWhen(e.target.value); setConflict(null); }}
              data-testid="manual-when-input"
              className="w-full px-3 py-2.5 bg-panel-soft text-ivory border border-line rounded-lg text-[15px] focus:outline-none"
            />
          )}
        </div>

        {conflict && (
          <ConflictPanel conflict={conflict} onFillDay={onFillDay ? fillDay : null} onFixBlocker={onOffenderJump} />
        )}

        <button
          onClick={save}
          disabled={busy}
          data-testid="manual-punch-save"
          className="seal-primary w-full rounded-lg py-3 text-[15px] font-semibold disabled:opacity-40"
        >
          Record punch
        </button>
      </div>
    </div>
  );
}

function EditEventModal({ event, onClose, onSaved, onOffenderJump }) {
  const [type, setType] = useState(event.type);
  const [when, setWhen] = useState(toLocalDatetimeInput(event.ts));
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);

  const save = async () => {
    setBusy(true);
    try {
      const body = {};
      if (type !== event.type) body.type = type;
      const iso = fromLocalDatetimeInput(when);
      if (iso && iso !== event.ts) body.ts = iso;
      if (Object.keys(body).length === 0) { onClose(); return; }
      // (b) Cross-day / future / far-past prompts on new ts
      if (iso && iso !== event.ts) {
        const chosen = new Date(iso);
        const orig = new Date(event.ts);
        const shiftHours = (chosen.getTime() - orig.getTime()) / 3600000;
        if (Math.abs(shiftHours) > 6) {
          if (!window.confirm(`You're moving this punch by ${shiftHours > 0 ? "+" : ""}${shiftHours.toFixed(1)} hours (from ${orig.toLocaleString()} to ${chosen.toLocaleString()}). Save anyway?`)) { setBusy(false); return; }
        } else if (chosen.toDateString() !== orig.toDateString()) {
          if (!window.confirm(`You're moving this punch from ${orig.toDateString()} to ${chosen.toDateString()}. Save anyway?`)) { setBusy(false); return; }
        }
        if (chosen.getTime() > Date.now() + 60 * 1000) {
          if (!window.confirm(`That new time is in the future. Save anyway?`)) { setBusy(false); return; }
        }
      }
      body.tz_offset_min = -new Date().getTimezoneOffset();
      await client.patch(`/events/${event.id}`, body);
      toast.success("Punch updated");
      onSaved && onSaved();
    } catch (e) {
      const c = readConflict(e);
      if (c) { setConflict(c); return; }
      const offender = getErrorOffender(e);
      if (offender && onOffenderJump) {
        toast.error(formatApiError(e), {
          duration: 8000,
          action: { label: "Fix blocker", onClick: () => onOffenderJump(offender) },
        });
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this punch record?")) return;
    setBusy(true);
    try {
      await client.delete(`/events/${event.id}`);
      toast.success("Punch deleted");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
      data-testid="edit-event-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-panel border border-line rounded-2xl w-full max-w-[480px] p-5"
        data-testid="edit-event-modal"
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="caps text-muted-jewel">Edit punch</div>
            <div className="font-serif text-xl text-ivory">{event.employee_name}</div>
            <div className="text-[12px] text-muted-jewel">
              {event.branch}
              {event.manual ? " · owner-added" : ""}
              {event.edited ? " · edited" : ""}
            </div>
          </div>
          <button onClick={onClose} data-testid="edit-event-close" className="text-muted-jewel hover:text-ivory text-xl leading-none px-2">×</button>
        </div>

        <div className="mb-3">
          <div className="caps text-muted-jewel text-[11px] mb-1.5">Type</div>
          <div className="grid grid-cols-2 gap-2">
            {PUNCH_TYPE_OPTIONS.map(([k, l]) => (
              <button
                key={k}
                onClick={() => { setType(k); setConflict(null); }}
                data-testid={`edit-type-${k}`}
                className={`rounded-lg py-2.5 text-[14px] font-semibold border transition-colors ${
                  type === k ? "seal-primary border-transparent" : "bg-transparent border-line text-muted-jewel hover:text-ivory"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <div className="caps text-muted-jewel text-[11px] mb-1.5">When</div>
          <input
            type="datetime-local"
            value={when}
            onChange={(e) => { setWhen(e.target.value); setConflict(null); }}
            data-testid="edit-when-input"
            className="w-full px-3 py-2.5 bg-panel-soft text-ivory border border-line rounded-lg text-[15px] focus:outline-none"
          />
        </div>

        {conflict && <ConflictPanel conflict={conflict} onFixBlocker={onOffenderJump} />}

        <div className="flex gap-2">
          <button
            onClick={save}
            disabled={busy}
            data-testid="edit-event-save"
            className="seal-primary flex-1 rounded-lg py-2.5 text-[15px] font-semibold disabled:opacity-40"
          >
            Save
          </button>
          <button
            onClick={remove}
            disabled={busy}
            data-testid="edit-event-delete"
            className="rounded-lg px-4 py-2.5 text-[13px] font-semibold text-jewel-red border border-jewel-red/60 hover:bg-jewel-red/10 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- This week ---------- */
/* ---------- Records ---------- */
function BulkPunchModal({ employees, onClose, onSaved, prefill }) {
  const [date, setDate] = useState(prefill?.date || ymd(new Date()));
  const [defaultIn, setDefaultIn] = useState(prefill?.inTime || "10:00");
  const [defaultOut, setDefaultOut] = useState(prefill?.outTime || "18:00");
  const [rows, setRows] = useState(() =>
    (employees || []).map((e) => ({ id: e.id, name: e.name, branch: e.branch, on: prefill ? e.id === prefill.employeeId : true, inTime: "", outTime: "" }))
  );
  const [busy, setBusy] = useState(false);

  const toggle = (id) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, on: !r.on } : r)));
  const setField = (id, k, v) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [k]: v } : r)));
  const selectedCount = rows.filter((r) => r.on).length;

  const apply = async () => {
    const active = rows.filter((r) => r.on);
    if (active.length === 0) { toast.error("Select at least one employee"); return; }
    setBusy(true);
    let okCount = 0, failCount = 0;
    const failures = [];
    for (const r of active) {
      const inHm = r.inTime || defaultIn;
      const outHm = r.outTime || defaultOut;
      const inIso = fromLocalDatetimeInput(`${date}T${inHm}`);
      const outIso = fromLocalDatetimeInput(`${date}T${outHm}`);
      try {
        await client.post("/events/manual-day", {
          employee_id: r.id, in_ts: inIso, out_ts: outIso, tz_offset_min: -new Date().getTimezoneOffset(),
        });
        okCount++;
      } catch (e) {
        failCount++;
        failures.push(`${r.name}: ${formatApiError(e)}`);
      }
    }
    setBusy(false);
    if (okCount) toast.success(`Filled ${okCount} employee${okCount === 1 ? "" : "s"}`);
    if (failCount) toast.error(`${failCount} failed — ${failures[0]}${failCount > 1 ? " (+more)" : ""}`, { duration: 8000 });
    if (failCount && prefill) return;
    onSaved && onSaved();
  };

  const inputCls = "w-full px-2 py-2 bg-panel-soft text-ivory border border-line rounded-lg text-[14px] focus:outline-none";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
      data-testid="bulk-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-panel border border-line rounded-2xl w-full max-w-[520px] max-h-[90vh] overflow-y-auto p-5"
        data-testid="bulk-modal"
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="caps text-muted-jewel">{prefill ? "Fill whole day" : "Bulk fill day"}</div>
            <div className="font-serif text-xl text-ivory">{prefill ? "Add the punch-in and punch-out together" : "Punch in + out for a group"}</div>
          </div>
          <button onClick={onClose} data-testid="bulk-close" className="text-muted-jewel hover:text-ivory text-xl leading-none px-2">×</button>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4">
          <div>
            <div className="caps text-muted-jewel text-[11px] mb-1">Date</div>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="bulk-date" className={inputCls} />
          </div>
          <div>
            <div className="caps text-muted-jewel text-[11px] mb-1">In</div>
            <input type="time" value={defaultIn} onChange={(e) => setDefaultIn(e.target.value)} data-testid="bulk-in" className={inputCls} />
          </div>
          <div>
            <div className="caps text-muted-jewel text-[11px] mb-1">Out</div>
            <input type="time" value={defaultOut} onChange={(e) => setDefaultOut(e.target.value)} data-testid="bulk-out" className={inputCls} />
          </div>
        </div>

        <div className="caps text-muted-jewel text-[11px] mb-1.5">
          Employees ({selectedCount} of {rows.length} selected)
        </div>
        <div className="border border-line rounded-lg divide-y divide-line max-h-[45vh] overflow-y-auto">
          {rows.map((r) => (
            <div key={r.id} className="p-2.5" data-testid={`bulk-row-${r.id}`}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={r.on}
                  onChange={() => toggle(r.id)}
                  data-testid={`bulk-check-${r.id}`}
                />
                <span className="text-ivory text-[14px] flex-1">
                  {r.name} <span className="text-muted-jewel text-[12px]">· {r.branch}</span>
                </span>
              </label>
              {r.on && (
                <div className="grid grid-cols-2 gap-2 mt-2 pl-6">
                  <input
                    type="time"
                    placeholder="Override in"
                    value={r.inTime}
                    onChange={(e) => setField(r.id, "inTime", e.target.value)}
                    data-testid={`bulk-in-${r.id}`}
                    className={inputCls + " text-[13px]"}
                  />
                  <input
                    type="time"
                    placeholder="Override out"
                    value={r.outTime}
                    onChange={(e) => setField(r.id, "outTime", e.target.value)}
                    data-testid={`bulk-out-${r.id}`}
                    className={inputCls + " text-[13px]"}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={apply}
          disabled={busy || selectedCount === 0}
          data-testid="bulk-apply"
          className="seal-primary w-full rounded-lg py-3 text-[15px] font-semibold disabled:opacity-40 mt-4"
        >
          {busy ? "Filling…" : `Apply to ${selectedCount} employee${selectedCount === 1 ? "" : "s"}`}
        </button>
        <p className="text-muted-jewel text-[11px] mt-2 text-center">
          Blank override = default. If a punch clashes with an existing record, that person is skipped and reported at the end.
        </p>
      </div>
    </div>
  );
}

export { ManualPunchModal, EditEventModal, BulkPunchModal };
